import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from './lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Solid aqua — primary action
        primary:
          'bg-aqua text-white hover:bg-teal-dark shadow-sm',
        // Aqua→royal gradient — hero/CTA
        hero:
          'bg-gradient-to-r from-aqua to-royal text-white hover:opacity-90 shadow-sm',
        // White with royal border — default outlined
        default:
          'bg-nn-white border border-royal text-royal hover:bg-surface shadow-sm',
        // Teal 2px border — save action
        save:
          'bg-nn-white border-2 border-teal-dark text-teal-dark hover:bg-green-bg',
        // Teal outline — create action
        create:
          'bg-nn-white border border-teal-dark text-teal-dark hover:bg-green-bg',
        // Green outline — success/confirm
        success:
          'bg-nn-white border border-green-nn text-green-nn hover:bg-green-bg',
        // Red outline — danger/delete
        danger:
          'bg-nn-white border border-red-critical text-red-critical hover:bg-red-bg',
        // Ghost — subtle, no background
        ghost:
          'hover:bg-surface text-text-dark hover:text-text-dark',
        // Link style
        link:
          'text-aqua underline-offset-4 hover:underline p-0 h-auto',
      },
      size: {
        default: 'h-8 px-3 py-1.5',
        sm:      'h-7 px-2.5 py-1 text-[12px]',
        lg:      'h-10 px-4 py-2 text-[14px]',
        icon:    'h-8 w-8 p-0',
        'icon-sm':'h-7 w-7 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
